import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import BulkOpsDialog from "@/components/BulkOpsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Baby, MoveRight, AlertCircle, Trash2, Wand2, Pencil } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

function calcAge(iso) {
  if (!iso) return "-";
  const b = new Date(iso);
  const t = new Date();
  let y = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) y--;
  return `${y} jr`;
}

export default function KinderenPage() {
  const { user } = useAuth();
  const canWrite = can(user, "children", "write");
  const canMove = user?.role === "superadmin" || user?.role === "locatiemanager";
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm());
  const [editingId, setEditingId] = useState(null);
  const [locFilter, setLocFilter] = useState("all");
  const [grpFilter, setGrpFilter] = useState("all");
  const [moveKid, setMoveKid] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null); // "delete" | "update"
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const canDelete = user?.role === "superadmin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, g, m] = await Promise.all([
        api.get("/children"),
        api.get("/locations"),
        api.get("/groups"),
        api.get("/medewerkers"),
      ]);
      setRows(c.data); setLocations(l.data); setGroups(g.data); setMedewerkers(m.data);
    } catch (e) {
      // Slik geen fout: laat de gebruiker weten dat er een probleem is.
      const { toast } = await import("sonner");
      toast.error(formatApiError(e.response?.data?.detail) || "Kon kinderen niet laden");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    // Client-side validatie: precies één van beide datums moet ingevuld zijn
    // conform de gekozen geboortestatus.
    if (form.geboorte_status === "geboren" && !form.geboortedatum) {
      toast.error("Vul de geboortedatum in"); return;
    }
    if (form.geboorte_status === "ongeboren" && !form.uitgerekende_datum) {
      toast.error("Vul de uitgerekende datum in"); return;
    }
    // Stuur alleen de relevante datum door (backend accepteert beide velden).
    const payload = { ...form };
    if (payload.geboorte_status === "geboren") payload.uitgerekende_datum = null;
    else payload.geboortedatum = null;
    try {
      if (editingId) {
        await api.put(`/children/${editingId}`, payload);
        toast.success("Kind bijgewerkt");
      } else {
        await api.post("/children", payload);
        toast.success("Kind toegevoegd");
      }
      setOpen(false); setForm(initialForm()); setEditingId(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (row) => {
    setForm({
      voornaam: row.voornaam || "",
      achternaam: row.achternaam || "",
      geboorte_status: row.geboorte_status || (row.geboortedatum ? "geboren" : "ongeboren"),
      geboortedatum: row.geboortedatum || "",
      uitgerekende_datum: row.uitgerekende_datum || "",
      bsn: row.bsn || "",
      location_id: row.location_id || "",
      group_id: row.group_id || "",
      parent_ids: row.parent_ids || [],
      mentor_medewerker_id: row.mentor_medewerker_id || "",
      huisarts: row.huisarts || "",
      tandarts: row.tandarts || "",
      allergies: row.allergies || "",
      notes: row.notes || "",
      toestemming_foto: !!row.toestemming_foto,
      toestemming_uitstapje: !!row.toestemming_uitstapje,
      pickup_persons: row.pickup_persons || [],
    });
    setEditingId(row.id);
    setOpen(true);
  };

  const locNameById = Object.fromEntries(locations.map(l => [l.id, l.name]));
  const grpNameById = Object.fromEntries(groups.map(g => [g.id, g.name]));
  const mwById = Object.fromEntries(medewerkers.map(m => [m.id, m]));

  const columns = [
    { key: "achternaam", label: "Achternaam", accessor: r => `${r.voornaam} ${r.achternaam}`, render: r => (
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] font-semibold text-xs">
          {r.voornaam?.[0]}{r.achternaam?.[0]}
        </div>
        <div>
          <div className="font-medium text-neutral-800">{r.voornaam} {r.achternaam}</div>
          <div className="text-xs text-neutral-500">{calcAge(r.geboortedatum)} · geb. {new Date(r.geboortedatum).toLocaleDateString('nl-NL')}</div>
        </div>
      </div>
    )},
    { key: "location_id", label: "Locatie", accessor: r => locNameById[r.location_id] || "-" },
    { key: "group_id", label: "Groep", accessor: r => grpNameById[r.group_id] || "-",
      render: r => grpNameById[r.group_id]
        ? <Badge variant="outline" className="border-neutral-200 text-neutral-700">{grpNameById[r.group_id]}</Badge>
        : <span className="text-neutral-400 text-xs">Niet toegewezen</span>
    },
    { key: "mentor_medewerker_id", label: "Mentor", accessor: r => {
        const m = mwById[r.mentor_medewerker_id]; return m ? `${m.voornaam} ${m.achternaam}` : "";
      }, render: r => {
        const m = mwById[r.mentor_medewerker_id];
        return m
          ? <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center bg-[color:var(--terracotta)] text-white font-semibold text-[10px]">
                {m.voornaam?.[0]}{m.achternaam?.[0]}
              </div>
              <span className="text-xs">{m.voornaam} {m.achternaam}</span>
            </div>
          : <span className="text-neutral-400 text-xs">— Niet toegewezen</span>;
      }},
    { key: "allergies", label: "Allergie", render: r => r.allergies
      ? <Badge className="bg-red-50 text-red-700 border-red-200 border" variant="outline" data-testid={`allergie-${r.id}`}>
          <AlertCircle className="h-3 w-3 mr-1" />{r.allergies}
        </Badge>
      : <span className="text-neutral-400 text-xs">—</span> },
  ];
  if (canWrite) {
    columns.push({
      key: "bewerken", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(r); }}
          data-testid={`edit-kind-${r.id}`}
          className="text-[color:var(--sage)] hover:bg-[color:var(--sage)]/10" title="Bewerk">
          <Pencil className="h-4 w-4" />
        </Button>
      ),
    });
  }
  if (canMove) {
    columns.push({
      key: "verplaats", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost"
          data-testid={`verplaats-${r.id}`}
          onClick={(e) => { e.stopPropagation(); setMoveKid(r); }}
          className="text-neutral-600 hover:text-[color:var(--sage)]"
          title="Verplaats naar andere groep/locatie">
          <MoveRight className="h-4 w-4" />
        </Button>
      ),
    });
  }

  // Filteren aan client-side (dataset is klein)
  const [searchParams, setSearchParams] = useSearchParams();
  const mentorMissingFilter = searchParams.get("mentor_missing") === "1";
  const filtered = rows.filter(r =>
    (locFilter === "all" || r.location_id === locFilter) &&
    (grpFilter === "all" || r.group_id === grpFilter) &&
    (!mentorMissingFilter || !r.mentor_medewerker_id)
  );

  return (
    <div>
      <PageHeader
        title="Kinderen"
        subtitle="Alle geregistreerde kinderen — filter per locatie/groep, snel doorzoekbaar."
        testid="kinderen-header"
        actions={canWrite && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(initialForm()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-kind-btn" style={{ background: "var(--sage)" }} className="text-white hover:brightness-110">
                <Plus className="h-4 w-4 mr-1" /> Nieuw kind
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg" data-testid="add-kind-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? "Kind bewerken" : "Nieuw kind toevoegen"}</DialogTitle>
                <DialogDescription>Vul de basisgegevens in. Later kun je toestemmingen en medische info aanvullen.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Voornaam</Label><Input data-testid="new-kind-voornaam" value={form.voornaam} onChange={e => setForm({ ...form, voornaam: e.target.value })} /></div>
                <div><Label>Achternaam</Label><Input data-testid="new-kind-achternaam" value={form.achternaam} onChange={e => setForm({ ...form, achternaam: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Geboortestatus *</Label>
                  <div className="flex gap-2 mt-1" data-testid="new-kind-geboorte-status">
                    {[
                      ["geboren", "Al geboren"],
                      ["ongeboren", "Nog niet geboren (uitgerekende datum)"],
                    ].map(([v, l]) => (
                      <button key={v} type="button"
                        onClick={() => setForm({
                          ...form, geboorte_status: v,
                          // Reset de andere datum zodat we niet per ongeluk 2 waarden bewaren
                          geboortedatum: v === "geboren" ? form.geboortedatum : "",
                          uitgerekende_datum: v === "ongeboren" ? form.uitgerekende_datum : "",
                        })}
                        className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${
                          form.geboorte_status === v
                            ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]"
                            : "border-neutral-200 hover:bg-neutral-100"
                        }`}
                        data-testid={`new-kind-status-${v}`}
                      >
                        {form.geboorte_status === v && <span className="mr-1">✓</span>}{l}
                      </button>
                    ))}
                  </div>
                </div>
                {form.geboorte_status === "ongeboren" ? (
                  <div>
                    <Label>Uitgerekende datum *</Label>
                    <Input type="date" data-testid="new-kind-uitgerekende-datum"
                           value={form.uitgerekende_datum || ""}
                           onChange={e => setForm({ ...form, uitgerekende_datum: e.target.value })} />
                  </div>
                ) : (
                  <div>
                    <Label>Geboortedatum *</Label>
                    <Input type="date" data-testid="new-kind-gebd"
                           value={form.geboortedatum}
                           onChange={e => setForm({ ...form, geboortedatum: e.target.value })} />
                  </div>
                )}
                <div><Label>BSN (optioneel)</Label><Input value={form.bsn || ""} onChange={e => setForm({ ...form, bsn: e.target.value })} /></div>
                <div>
                  <Label>Locatie</Label>
                  <Select value={form.location_id} onValueChange={v => setForm({ ...form, location_id: v, group_id: "" })}>
                    <SelectTrigger data-testid="new-kind-location"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Groep</Label>
                  <Select value={form.group_id} onValueChange={v => setForm({ ...form, group_id: v })} disabled={!form.location_id}>
                    <SelectTrigger><SelectValue placeholder="Kies groep" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {groups.filter(g => g.location_id === form.location_id).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Allergieën</Label><Input value={form.allergies || ""} onChange={e => setForm({ ...form, allergies: e.target.value })} placeholder="Bijv. noten, lactose" /></div>
                <div className="col-span-2">
                  <Label>Mentor (medewerker)</Label>
                  <Select value={form.mentor_medewerker_id || ""} onValueChange={v => setForm({ ...form, mentor_medewerker_id: v })}>
                    <SelectTrigger data-testid="new-kind-mentor"><SelectValue placeholder="Kies mentor" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {medewerkers
                        .filter(m => !form.location_id || (m.location_ids || []).includes(form.location_id))
                        .map(m => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam} — {m.functie}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Notities</Label><Textarea rows={2} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="col-span-2 flex items-center gap-6 mt-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.toestemming_foto} onCheckedChange={v => setForm({ ...form, toestemming_foto: !!v })} /> Toestemming foto
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.toestemming_uitstapje} onCheckedChange={v => setForm({ ...form, toestemming_uitstapje: !!v })} /> Toestemming uitstapje
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); setForm(initialForm()); }}>Annuleren</Button>
                <Button onClick={submit} data-testid="new-kind-submit" style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {/* Filter-strip */}
      <div className="mb-4 sm:mb-6 flex flex-wrap items-center gap-2" data-testid="kinderen-filters">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mr-1">Filter</div>
        <Select value={locFilter} onValueChange={(v) => { setLocFilter(v); setGrpFilter("all"); }}>
          <SelectTrigger className="w-52 h-9" data-testid="filter-locatie">
            <SelectValue placeholder="Locatie" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Alle locaties</SelectItem>
            {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={grpFilter} onValueChange={setGrpFilter}>
          <SelectTrigger className="w-52 h-9" data-testid="filter-groep">
            <SelectValue placeholder="Groep" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">Alle groepen</SelectItem>
            {groups.filter(g => locFilter === "all" || g.location_id === locFilter).map(g => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-neutral-500 ml-1" data-testid="kinderen-count">{filtered.length} van {rows.length} kinderen</div>
        {mentorMissingFilter && (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200 cursor-pointer hover:bg-red-100"
            onClick={() => { const s = new URLSearchParams(searchParams); s.delete("mentor_missing"); setSearchParams(s); }}
            data-testid="filter-mentor-missing-chip"
          >
            Alleen zonder mentor · klik om te wissen ✕
          </Badge>
        )}
      </div>

      {canMove && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 p-2.5 bg-[color:var(--sage-100)]/40 border border-[color:var(--sage-100)] rounded-md" data-testid="kinderen-bulk-toolbar">
          <span className="text-sm font-medium text-[color:var(--sage)]" data-testid="kinderen-bulk-count">{selected.size} geselecteerd</span>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="kinderen-bulk-clear">Selectie wissen</Button>
          <div className="flex-1" />
          <Button size="sm" onClick={() => setBulkMode("update")} data-testid="kinderen-bulk-update" style={{ background: "var(--sage)" }} className="text-white">
            <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Wijzig {selected.size}
          </Button>
          {canDelete && (
            <Button size="sm" onClick={() => setBulkMode("delete")} data-testid="kinderen-bulk-delete"
                    className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Verwijder {selected.size}
            </Button>
          )}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="mb-6 flex items-center gap-3 p-4 rounded-md bg-[color:var(--sage-100)]/40 border border-[color:var(--sage-100)]">
          <Baby className="h-5 w-5 text-[color:var(--sage)]" />
          <div className="text-sm text-neutral-700">Nog geen kinderen zichtbaar met je huidige rol/locatie. {canWrite && "Voeg er een toe om te beginnen."}</div>
        </div>
      )}

      <DataTable columns={columns} rows={filtered} testid="kinderen-table"
                 selectable={canMove} selected={selected} onSelectionChange={setSelected}
                 loading={loading} skeletonRows={8}
                 onRowClick={r => setDetail(r)} />

      <BulkOpsDialog
        open={!!bulkMode}
        onClose={() => setBulkMode(null)}
        mode={bulkMode}
        resource="children"
        ids={[...selected]}
        onDone={() => { setSelected(new Set()); load(); }}
        updateFields={[
          { key: "location_id", label: "Verplaats naar locatie", type: "select", allowClear: false,
            options: locations.map(l => ({ value: l.id, label: l.name })) },
          { key: "group_id", label: "Verplaats naar groep", type: "select", allowClear: false,
            options: groups.map(g => ({ value: g.id, label: `${g.name} — ${locNameById[g.location_id] || "?"}` })) },
          { key: "mentor_medewerker_id", label: "Wijs mentor toe", type: "select", allowClear: true,
            options: medewerkers.map(m => ({ value: m.id, label: `${m.voornaam} ${m.achternaam}` })) },
        ]}
      />

      {/* Verplaats-dialog — alleen admin/locatiemanager */}
      <VerplaatsDialog
        kid={moveKid}
        locations={locations}
        groups={groups}
        onClose={() => setMoveKid(null)}
        onDone={() => { setMoveKid(null); load(); }}
      />

      {/* Kind-detail modal — professionele quickview met tabs */}
      <KindDetailModal
        kind={detail}
        onClose={() => setDetail(null)}
        locNameById={locNameById}
        grpNameById={grpNameById}
        mwById={mwById}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Kind-detail modal met tabs (Overzicht / Contract / Ophaal-lijst / Notities)
// ---------------------------------------------------------------------------

function KindDetailModal({ kind, onClose, locNameById, grpNameById, mwById }) {
  const [contracts, setContracts] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overzicht");

  useEffect(() => {
    if (!kind) return;
    setTab("overzicht");
    setLoading(true);
    Promise.all([
      api.get(`/contracts?kind_id=${kind.id}`).catch(() => ({ data: [] })),
      api.get("/parents").catch(() => ({ data: [] })),
    ]).then(([c, p]) => {
      setContracts(c.data || []);
      setParents((p.data || []).filter(o => (kind.parent_ids || []).includes(o.id)));
    }).finally(() => setLoading(false));
  }, [kind?.id]);

  if (!kind) return null;
  const mentor = mwById[kind.mentor_medewerker_id];
  const activeContract = contracts.find(c => c.status === "actief") || contracts[0];
  const geboorteVeld = kind.geboorte_status === "ongeboren" ? kind.uitgerekende_datum : kind.geboortedatum;
  const dagenLabels = ["ma", "di", "wo", "do", "vr"];

  return (
    <Dialog open={!!kind} onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="kind-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] font-semibold">
              {kind.voornaam?.[0]}{kind.achternaam?.[0]}
            </div>
            <div>
              <div>{kind.voornaam} {kind.achternaam}</div>
              <div className="text-xs font-normal text-neutral-500">
                {calcAge(kind.geboortedatum)} · {locNameById[kind.location_id] || "—"} · {grpNameById[kind.group_id] || "geen groep"}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Kind-detailoverzicht met tabs voor contract, ophaal-lijst en notities.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-neutral-100 -mx-6 px-6" data-testid="kind-detail-tabs">
          {[
            ["overzicht", "Overzicht"],
            ["contract", "Contract"],
            ["ophaal", "Ophaal-lijst"],
            ["notities", "Notities"],
          ].map(([id, label]) => (
            <button key={id} type="button"
              onClick={() => setTab(id)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === id
                  ? "border-[color:var(--sage)] text-[color:var(--sage)] font-medium"
                  : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}
              data-testid={`kind-detail-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-[60vh] overflow-y-auto text-sm">
          {tab === "overzicht" && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 py-2" data-testid="kind-detail-overzicht">
              <div>
                <span className="text-neutral-500">Geboortestatus:</span>{" "}
                <Badge variant="outline" data-testid="kind-detail-geboorte-status">
                  {kind.geboorte_status === "ongeboren" ? "Nog niet geboren" : "Geboren"}
                </Badge>
              </div>
              <div>
                <span className="text-neutral-500">
                  {kind.geboorte_status === "ongeboren" ? "Uitgerekende datum" : "Geboortedatum"}:
                </span>{" "}
                {geboorteVeld
                  ? new Date(geboorteVeld).toLocaleDateString("nl-NL")
                  : <span className="text-neutral-400">—</span>}
              </div>
              <div><span className="text-neutral-500">Locatie:</span> {locNameById[kind.location_id] || "—"}</div>
              <div><span className="text-neutral-500">Groep:</span> {grpNameById[kind.group_id] || "—"}</div>
              <div className="col-span-2"><span className="text-neutral-500">Mentor:</span>{" "}
                {mentor
                  ? <span className="inline-flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[color:var(--terracotta)] text-white font-semibold text-[9px]">
                        {mentor.voornaam?.[0]}{mentor.achternaam?.[0]}
                      </div>
                      {mentor.voornaam} {mentor.achternaam}
                    </span>
                  : <span className="text-neutral-400">Niet toegewezen</span>}
              </div>
              <div><span className="text-neutral-500">Huisarts:</span> {kind.huisarts || <span className="text-neutral-400">—</span>}</div>
              <div><span className="text-neutral-500">Tandarts:</span> {kind.tandarts || <span className="text-neutral-400">—</span>}</div>
              {kind.allergies && (
                <div className="col-span-2 flex items-start gap-1.5 mt-1 p-2 rounded bg-amber-50 border border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-medium text-amber-800">Allergie</div>
                    <div className="text-amber-700 text-xs">{kind.allergies}</div>
                  </div>
                </div>
              )}
              <div className="col-span-2 flex gap-3 pt-1">
                <span className={`text-xs px-2 py-0.5 rounded ${kind.toestemming_foto ? "bg-emerald-50 text-emerald-700" : "bg-neutral-50 text-neutral-500"}`}>
                  {kind.toestemming_foto ? "✓" : "✕"} Foto-toestemming
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${kind.toestemming_uitstapje ? "bg-emerald-50 text-emerald-700" : "bg-neutral-50 text-neutral-500"}`}>
                  {kind.toestemming_uitstapje ? "✓" : "✕"} Uitstapje-toestemming
                </span>
              </div>
            </div>
          )}

          {tab === "contract" && (
            <div className="py-2 space-y-3" data-testid="kind-detail-contract">
              {loading ? (
                <div className="text-neutral-400 text-center py-6">Contract laden…</div>
              ) : contracts.length === 0 ? (
                <div className="text-neutral-500 text-center py-6">Geen contract gevonden voor dit kind.</div>
              ) : (
                <>
                  {activeContract && (
                    <div className="border rounded-md p-3 bg-[color:var(--sage-100)]/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">Actief contract</div>
                        <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                          {activeContract.status || "actief"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-neutral-500">Van:</span> {activeContract.start_date}</div>
                        <div><span className="text-neutral-500">Tot:</span> {activeContract.end_date || "doorlopend"}</div>
                        <div><span className="text-neutral-500">Uurtarief:</span> € {(activeContract.uurtarief || 0).toFixed(2)}</div>
                        <div><span className="text-neutral-500">Uren/week:</span> {(activeContract.uren_per_week || 0).toFixed(1)}</div>
                        <div className="col-span-2">
                          <span className="text-neutral-500">Dagen:</span>{" "}
                          <span className="inline-flex gap-1 flex-wrap">
                            {(activeContract.dagen || []).map((d, i) => (
                              <Badge key={`${d.weekday}-${d.dagdeel}-${i}`} variant="outline" className="text-[10px]">
                                {dagenLabels[d.weekday]} · {d.dagdeel}
                              </Badge>
                            ))}
                            {(!activeContract.dagen || activeContract.dagen.length === 0) &&
                              <span className="text-neutral-400">geen dagen</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {contracts.length > 1 && (
                    <div>
                      <div className="text-xs text-neutral-500 mb-1 uppercase tracking-wide">Historie</div>
                      {contracts.filter(c => c.id !== activeContract?.id).map(c => (
                        <div key={c.id} className="text-xs flex items-center justify-between border-b border-neutral-100 py-1">
                          <span>{c.start_date} → {c.end_date || "doorlopend"}</span>
                          <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "ophaal" && (
            <div className="py-2 space-y-3" data-testid="kind-detail-ophaal">
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Ouders (standaard ophalers)</div>
                {parents.length === 0 ? (
                  <div className="text-neutral-400 text-xs">Geen ouder-koppeling</div>
                ) : (
                  parents.map(p => (
                    <div key={p.id} className="flex items-center justify-between border-b border-neutral-100 py-1.5">
                      <div>
                        <div className="font-medium">{p.voornaam} {p.achternaam}</div>
                        {p.email && <div className="text-xs text-neutral-500">{p.email}</div>}
                      </div>
                      {p.telefoon && (
                        <a href={`tel:${p.telefoon}`} className="text-xs text-[color:var(--sage)] underline">
                          {p.telefoon}
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Extra bevoegde ophalers</div>
                {(kind.pickup_persons || []).length === 0 ? (
                  <div className="text-neutral-400 text-xs">Geen extra ophalers geregistreerd</div>
                ) : (
                  (kind.pickup_persons || []).map((pp, i) => (
                    <div key={`${pp.naam}-${pp.telefoon || i}`} className="flex items-center justify-between border-b border-neutral-100 py-1.5">
                      <div>
                        <div className="font-medium">{pp.naam}</div>
                        {pp.relatie && <div className="text-xs text-neutral-500">{pp.relatie}</div>}
                      </div>
                      {pp.telefoon && (
                        <a href={`tel:${pp.telefoon}`} className="text-xs text-[color:var(--sage)] underline">
                          {pp.telefoon}
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "notities" && (
            <div className="py-2" data-testid="kind-detail-notities">
              {kind.notes
                ? <div className="whitespace-pre-wrap text-neutral-700 leading-relaxed p-3 rounded bg-neutral-50 border border-neutral-100">
                    {kind.notes}
                  </div>
                : <div className="text-neutral-400 text-center py-6">Geen notities voor dit kind.</div>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="kind-detail-close">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dialog waarmee admin/locatiemanager een kind kan verplaatsen naar een andere groep en/of locatie.
 *  Wijziging is transparant voor eind-gebruikers maar wordt volledig in het audit-logboek vastgelegd. */
function VerplaatsDialog({ kid, locations, groups, onClose, onDone }) {
  const [newLoc, setNewLoc] = useState("");
  const [newGrp, setNewGrp] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (kid) { setNewLoc(kid.location_id || ""); setNewGrp(kid.group_id || ""); setReason(""); }
  }, [kid?.id]);

  if (!kid) return null;
  const grpsForLoc = groups.filter(g => g.location_id === newLoc);
  const changed = newLoc !== kid.location_id || newGrp !== kid.group_id;

  const save = async () => {
    if (!changed) { toast.info("Geen wijziging"); return; }
    setBusy(true);
    try {
      await api.post(`/children/${kid.id}/move`, { location_id: newLoc, group_id: newGrp, reason });
      toast.success(`${kid.voornaam} ${kid.achternaam} verplaatst`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verplaatsing mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!kid} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="verplaats-dialog">
        <DialogHeader>
          <DialogTitle>{kid.voornaam} {kid.achternaam} verplaatsen</DialogTitle>
          <DialogDescription>
            Wijziging wordt gelogd in het audit-logboek maar is verder niet zichtbaar op het kind-record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nieuwe locatie</Label>
            <Select value={newLoc} onValueChange={(v) => { setNewLoc(v); setNewGrp(""); }}>
              <SelectTrigger data-testid="verplaats-locatie"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nieuwe groep</Label>
            <Select value={newGrp} onValueChange={setNewGrp} disabled={!newLoc}>
              <SelectTrigger data-testid="verplaats-groep"><SelectValue placeholder="Kies groep" /></SelectTrigger>
              <SelectContent className="bg-white">
                {grpsForLoc.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Reden (optioneel)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Bijv. doorstroming naar peutergroep" data-testid="verplaats-reden" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={!changed || busy} data-testid="verplaats-submit"
            style={{ background: "var(--sage)" }} className="text-white">
            {busy ? "Bezig…" : "Verplaats"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialForm() {
  return {
    voornaam: "", achternaam: "",
    geboorte_status: "geboren", geboortedatum: "", uitgerekende_datum: "", bsn: "",
    location_id: "", group_id: "", parent_ids: [],
    mentor_medewerker_id: "",
    huisarts: "", tandarts: "", allergies: "", notes: "",
    toestemming_foto: false, toestemming_uitstapje: false,
  };
}
